// Plain words for employee separation (HR revamp Batch 5). The server sends stable codes and ISO
// dates; people see these words and readable dates, never the codes.
import { formatDay } from "./myHrText";

export const REASON_TYPES = [
  { value: "resignation", label: "Resignation" },
  { value: "end_of_contract", label: "End of contract" },
  { value: "dismissal", label: "Dismissal" },
  { value: "retirement", label: "Retirement" },
  { value: "other", label: "Other" },
];

export function reasonTypeLabel(value) {
  return REASON_TYPES.find((item) => item.value === value)?.label || "Other";
}

const ISO_DATE = /\b(\d{4}-\d{2}-\d{2})\b/g;

/** "Weekly schedule ends on 2026-10-15" → "Weekly schedule ends on Thu, Oct 15, 2026". */
export function readableDates(text) {
  if (!text) return text;
  return String(text).replace(ISO_DATE, (match) => formatDay(match, { year: true }));
}

/** Clearance groups in the order people act on them. */
export const CLEARANCE_GROUPS = [
  { key: "blocked", title: "Must be resolved first", hint: "The separation cannot be completed while any of these is open.", tone: "bad" },
  { key: "decide", title: "Decide first", hint: "These change the days and hours that count for final pay.", tone: "warn" },
  { key: "automatic", title: "Done automatically when the separation completes", hint: "Recorded in the employee's history.", tone: "muted" },
  { key: "info", title: "For your information", hint: "Shown only. Nothing here is paid out or settled by this step.", tone: "info" },
];

/**
 * What the primary button does for a new separation, from the chosen last working day.
 * A future day schedules it; today or a past day separates now when the checklist is clear,
 * otherwise records it and waits until the listed items are resolved.
 */
export function primaryActionFor({ lastWorkingDate, today, canComplete }) {
  if (!lastWorkingDate || !today) return { label: "Schedule separation", mode: "schedule" };
  if (lastWorkingDate > today) return { label: "Schedule separation", mode: "schedule" };
  if (canComplete) return { label: "Separate now", mode: "now" };
  return { label: "Record and finish later", mode: "waiting" };
}

/** One sentence for the result of recording a separation. */
export function outcomeMessage(outcome, name, lastWorkingDate) {
  const day = formatDay(lastWorkingDate, { year: true });
  if (outcome === "completed") return `${name} has been separated. Their last working day was ${day}.`;
  if (outcome === "waiting") return `Separation recorded. It completes once the items listed under "Must be resolved first" and "Decide first" are done.`;
  return `Separation scheduled. ${name} stays active until the end of ${day}.`;
}

export function statusSummary({ separation, today }) {
  if (!separation) return null;
  if (separation.status === "completed") {
    return separation.completionMode === "automatic"
      ? "Completed automatically after the last working day"
      : `Completed${separation.completedByName ? ` by ${separation.completedByName}` : ""}`;
  }
  if (separation.status === "cancelled") return "Cancelled";
  if (separation.lastWorkingDate > today) return `Scheduled. Stays active until the end of ${formatDay(separation.lastWorkingDate, { year: true })}`;
  return "Due. Waiting for the checklist to be cleared";
}
